import React from 'react';
import classNames from 'classnames';

import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table/index';
import TableBody from '@material-ui/core/TableBody/index';
import TableCell from '@material-ui/core/TableCell/index';
import TableRow from '@material-ui/core/TableRow/index';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { TableContainer } from "@material-ui/core";

import StatisticItem from './HomeStatisticItem';
import { TStatistics } from '../../../types/IHomeStore';
import SummaryCard from '../../Statistics/StatisticsTable/SummaryCard';

const styles = (theme: Theme) => createStyles({
  // Десктопная версия
  desktopWrapper: {
    [theme.breakpoints.down(960)]: {
      display: 'none!important',
    },
  },
  statistic: {
    display: 'flex',
    justifyContent: 'center',
    minHeight: 140,
  },
  statisticItem: {
    padding: '24px 10px',
  },
  tableHeader: {
    fontWeight: 500,
    padding: 24,
  },
  // Мобильная версия
  mobileCard: {
    display: 'none',
    [theme.breakpoints.down(960)]: {
      display: 'block',
    },
  },
  cardContent: {
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 0,
  },
});

interface IProps extends WithStyles<typeof styles> {
  statistic?: TStatistics;
  moneySymbol: string;
}

const HomeStatisticsTable: React.FC<IProps> = ({ classes, statistic, moneySymbol }) => {
  const renderStats = statistic || {
    period: {
      totalSuccessful: 0,
      totalExpectingGuests: 0,
      totalClosed: 0,
      totalCancelledByGuest: 0,
      totalCancelledByRestaurant: 0,
      totalCanceled: 0,
      totalSkipped: 0,
      averageReceiptAmount: 0,
    },
    percent: {
      totalSuccessful: 0,
      totalExpectingGuests: 0,
      totalClosed: 0,
      totalCancelledByGuest: 0,
      totalCancelledByRestaurant: 0,
      totalCanceled: 0,
      totalSkipped: 0,
      averageReceiptAmount: 0,
    },
    previous: {
      totalSuccessful: 0,
      totalExpectingGuests: 0,
      totalClosed: 0,
      totalCancelledByGuest: 0,
      totalCancelledByRestaurant: 0,
      totalCanceled: 0,
      totalSkipped: 0,
      averageReceiptAmount: 0,
    },
  };

  // Форматирование значения с метрикой для мобильной версии
  const formatValueWithMetric = (value: number, metric?: number): string => {
    const metricStr = metric !== undefined ? ` (${metric > 0 ? '+' : ''}${metric}%)` : '';
    return `${value}${metricStr}`;
  };

  return (
    <>
      {/* Десктопная версия (таблица) */}
      <div className={classNames(classes.statistic, classes.desktopWrapper)}>
        <TableContainer>
          <Table>
          <TableBody>
            <TableRow key="1">
              <TableCell
                className={classes.tableHeader}
                component="th"
                scope="row"
              >
                Успешные
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title="ВСЕГО"
                  type="allReserve"
                  stat={renderStats.period.totalSuccessful}
                  metric={renderStats.percent.totalSuccessful}
                />
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title="Ждем гостей"
                  type="waitingGuests"
                  stat={renderStats.period.totalExpectingGuests}
                />
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title="Счет закрыт"
                  type="closedInvoice"
                  stat={renderStats.period.totalClosed}
                />
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title={`Средний чек, ${moneySymbol}`}
                  type="averageCheck"
                  stat={renderStats.period.averageReceiptAmount}
                  metric={renderStats.percent.averageReceiptAmount}
                />
              </TableCell>
            </TableRow>
            <TableRow key="2">
              <TableCell
                className={classes.tableHeader}
                component="th"
                scope="row"
              >
                Отмена
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title="ВСЕГО"
                  type="allRejectsReserve"
                  stat={renderStats.period.totalCanceled}
                  metric={renderStats.percent.totalCanceled}
                />
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title="Пропущено"
                  type="missed"
                  stat={renderStats.period.totalSkipped}
                />
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title="Отмена гостем"
                  type="totalCancelledByGuest"
                  stat={renderStats.period.totalCancelledByGuest}
                />
              </TableCell>
              <TableCell className={classes.statisticItem}>
                <StatisticItem
                  title="Отмена рест."
                  type="totalCancelledByRestaurant"
                  stat={renderStats.period.totalCancelledByRestaurant}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        </TableContainer>
      </div>

      {/* Мобильная версия (карточки) */}
      <Card className={classes.mobileCard} elevation={0}>
        <CardContent className={classes.cardContent}>
          <SummaryCard 
            badge="Успешные" 
            items={[
              {
                title: "ВСЕГО",
                value: formatValueWithMetric(
                  renderStats.period.totalSuccessful,
                  renderStats.percent.totalSuccessful
                ),
                isError: renderStats.percent.totalSuccessful !== undefined && renderStats.percent.totalSuccessful < 0,
              },
              {
                title: "Ждем гостей",
                value: renderStats.period.totalExpectingGuests,
              },
              {
                title: "Счет закрыт",
                value: renderStats.period.totalClosed,
              },
              {
                title: `Средний чек, ${moneySymbol}`,
                value: formatValueWithMetric(
                  renderStats.period.averageReceiptAmount,
                  renderStats.percent.averageReceiptAmount
                ),
                isError: renderStats.percent.averageReceiptAmount !== undefined && renderStats.percent.averageReceiptAmount < 0,
              },
            ]} 
          />

          <SummaryCard 
            badge="Отмена" 
            items={[
              {
                title: "ВСЕГО",
                value: formatValueWithMetric(
                  renderStats.period.totalCanceled,
                  renderStats.percent.totalCanceled
                )              },
              {
                title: "Пропущено",
                value: renderStats.period.totalSkipped,
              },
              {
                title: "Отмена гостем",
                value: renderStats.period.totalCancelledByGuest,
              },
              {
                title: "Отмена рест.",
                value: renderStats.period.totalCancelledByRestaurant,
                isError: true,
              },
            ]} 
          />
        </CardContent>
      </Card>
    </>
  );
};

export default withStyles(styles)(HomeStatisticsTable);
